/**
 * The menus (08 §1-2): everything outside a mission, as plain DOM on a 640x360 canvas scaled by
 * the game's integer pixel scale. The screens call only `GameApi`; they hold no game state of
 * their own, so every screen is rebuilt from `api.profile()` each time it is shown.
 *
 * This file is the public face: the two entry points the app uses (`showTitle`,
 * `showAftermath`), the ports it may inject, the settings it subscribes to, and the `?ui=`
 * fixture routes. The machinery is in menusCore.ts; the flow between screens is in flow.ts.
 */

import type { Deployment, GameApi } from '../../meta/api';
import type { Aftermath } from '../../meta/types';
import type { MissionResult } from '../../sim/types';
import type { SuspendedMission } from './context';
import type { MenuPorts } from './controls/bindingsPort';
import { mountMenus } from './menusCore';
import { getMenuSettings } from './settings';

export { briefingText } from './briefing/briefingText';
export { keyName } from './controls/keyNames';
export { utcDate } from './daily/utcDate';
export { FIXTURE_NAMES, showFixture } from './fixtures/showFixture';
export { chooseLayout } from './scale';
export { getMenuSettings } from './settings';
export type { CloudPort, CloudStateName, CloudStatusView } from './cloud/cloudPort';
export type { SuspendedMission } from './context';
export type { BindingsPort, BoundAction, ControlScheme, MenuPorts } from './controls/bindingsPort';
export type { GameSpeed, Settings, SettingsStore, TextSize } from './settings';

export interface Menus {
  /** Show the title screen (and hide anything else). */
  showTitle(): void;
  /**
   * After a mission: the debrief, promotion/perk choice, medals, memorial if KIA - then back to
   * the theatre map (or the classic roster / practice picker / Daily Drop, by `deployment.mode`).
   */
  showAftermath(deployment: Deployment, result: MissionResult, aftermath: Aftermath | null, note?: string): void;
  hide(): void;
  dispose(): void;
}

/**
 * `onFly` is called when the player commits to a mission; the caller hides the menus and runs it.
 * `ports.bindings` is the input layer's key-binding store: without it the Controls screen can
 * only say that bindings are not available. `ports.cloud` is the app's cloud-save sync: without
 * it the Cloud Save screen only says that saves are kept in this browser.
 */
export function createMenus(
  root: HTMLElement,
  api: GameApi,
  onFly: (deployment: Deployment) => void,
  suspended: () => SuspendedMission | null = () => null,
  ports: MenuPorts = {},
): Menus {
  const m = mountMenus({ root, api, settings: getMenuSettings(), onFly, suspended, ports });
  return {
    showTitle(): void {
      if (m.isDisposed()) return;
      m.setVisible(true);
      m.ctx.nav.title();
    },
    showAftermath(deployment, result, aftermath, note): void {
      if (m.isDisposed()) return;
      m.setVisible(true);
      // ROUND7 §5: a training course the range officer stopped says why on its end card.
      const input = { deployment, result, aftermath };
      m.ctx.nav.aftermath(note === undefined ? input : { ...input, note });
    },
    hide(): void {
      if (!m.isDisposed()) m.setVisible(false);
    },
    dispose: () => m.dispose(),
  };
}
